import { VariantFilter } from "@/models/VariantFilter";
import { Helpers } from "@/global-scripts/helpers";
import { BaseContentModel } from "@/models/BaseContentModel";
import { TopicModel } from "@/models/TopicModel";
import { CategoryModel } from "@/models/CategoryModel";
import { Config } from "@/settings/Config";
import { NewsValidity } from "@/models/NewsValidity";
import {AllValidityDomainsDto, ValidityDomains, ValidityDomainValues} from "@/settings/ValidityDomains";

export class ValidityHelper {
  private readonly config = new Config();

  public getValidityEntryByUrlFriendlyTitle(
    validities: AllValidityDomainsDto,
    domain: ValidityDomainValues,
    title: string
  ): string | undefined {
    const entries = validities[domain];

    if (entries) {
      return entries.find((e) => {
        return Helpers.urlFriendlyName(e) === title;
      });
    }

    return undefined;
  }

  public isValidFilter(validities: string[], filter: VariantFilter | undefined): boolean {
    if (!filter) {
      return false;
    }

    return validities.find((v) => v === filter.entry) !== undefined;
  }

  public shouldResetFilterIfInvalid(validities: string[], filter: VariantFilter | undefined): boolean {
    return filter !== undefined && validities.length > 0;
  }

  catIncludeNews(category: CategoryModel, filters: VariantFilter<string | string[]>[]) {
    for (let i = 0; i < (category.topics || []).length; i++) {
      const item = category.topics[i];

      if (this.topicIncludeNews(item, filters)) {
        return true;
      }
    }

    for (let i = 0; i < (category.nodes || []).length; i++) {
      if (this.catIncludeNews(category.nodes[i], filters)) {
        return true;
      }
    }

    return false;
  }

  topicIncludeNews(topic: TopicModel, filters: VariantFilter<string | string[]>[]): boolean {
    const structureWeek = filters.find((f) => f.domain === ValidityDomains.structureWeek);

    if (!structureWeek) {
      return false;
    }

    if (topic.newsValidities) {
      const newsValidity = topic.newsValidities.find((n) => {
        return n.structureWeek === this.getNewsValidityEntry(structureWeek.entry as string);
      });

      if (!newsValidity) {
        return false;
      }

      if (this.matchNewsValidity(filters, newsValidity)) {
        return true;
      }
    }

    return false;
  }

  getNewsValidityEntry(structureWeek: string): string {
    return `News${structureWeek}`;
  }

  filterTopics(topics: TopicModel[], filters: VariantFilter[], onlyShowNews: boolean): TopicModel[] {
    const result = this.filterValidities(topics, filters);
    return onlyShowNews ? result.filter((t) => this.topicIncludeNews(t, filters)) : result;
  }

  filterCategories(categories: CategoryModel[], filters: VariantFilter[]) {
    const filteredCategories = this.filterValidities(categories, filters);

    filteredCategories.forEach((category) => {
      if (category.nodes) {
        category.nodes = this.filterValidities(category.nodes, filters);
      }
    });

    return filteredCategories;
  }

  populateCategoryTopics(categories: CategoryModel[], topics: TopicModel[]) {
    if (!categories) {
      return categories;
    }

    categories.forEach((category) => {
      category.topics = this.getTopicsByCategory(category, topics);

      this.populateCategoryTopics(category.nodes, topics);
    });
  }

  getTopicsByCategory(category: CategoryModel, topics: TopicModel[]): TopicModel[] {
    return topics.filter((topic) => topic.parentNodes && topic.parentNodes.indexOf(category.id) !== -1);
  }

  private filterValidities<T extends BaseContentModel>(contentData: T[], filters: VariantFilter[]): T[] {
    return contentData.filter((item: T) => this.matchValidity(filters, item.validities));
  }

  categoryHasTopics(category: CategoryModel) {
    if (category.topics && category.topics.length > 0) {
      return true;
    } else if (category.nodes) {
      for (let i = 0; i < category.nodes.length; i++) {
        const hasTopics = this.categoryHasTopics(category.nodes[i]);
        if (hasTopics) {
          return true;
        }
      }
    }
    return false;
  }

  matchNewsValidity(filters: VariantFilter<string | string[]>[], newsValidity: NewsValidity): boolean {
    for (let i = 0; i < newsValidity.validities.length; i++) {
      const val = newsValidity.validities[i];
      if (this.matchValidity(filters, newsValidity.validities[i])) {
        return true;
      }
    }
  }

  matchValidity(filters: VariantFilter<string | string[]>[], validities: Record<string, string[]>): boolean {
    if (!validities || Object.keys(validities).length === 0) {
      return true;
    }

    const matched: { [key: string]: boolean } = {};

    for (const domain in validities) {
      const filter = filters.find((f) => f.domain === domain);
      const domainFilter = validities[domain];

      if (domainFilter && domainFilter.length > 0 && filter) {
        //
        // Domain filter set => check if has match
        //
        const filterDomain = filters.find((f) => f.domain === domain) as VariantFilter<string | string[]>;
        const domainEntries = Array.isArray(filter.entry) ? (filter.entry as string[]) : [filter.entry as string];

        // Pre-check on first item. Domain can not have both positive and negative
        const positive = domainFilter[0].indexOf("!") === -1;

        // let groupHasPositiveMatch = false
        // let groupHasNegativeMatch = false

        /**********
         * Inline filter check function
         * -- Validates on the domain filter (string)
         * @param _domain
         * @param _filterDomain
         */
        const checkFilter = (_domain: string, _filterDomain: string) => {
          const _domainFilter = validities[_domain];

          for (let i = 0; i < _domainFilter.length; i++) {
            const validity = _domainFilter[i];
            const validityId = validity.replace("!", "");

            // if (positive && _filterDomain === entryId) {
            //   return true
            // } else {
            //   return false;
            // }

            if (positive && _filterDomain === validityId) {
              return true;
            } else if (!positive && _filterDomain === validityId) {
              return false;
            }
          }

          return !positive;
        };

        /**********
         * Inline domain entries check function
         * -- Validates on the domain group's entries (array)
         * -- At least one entry should validate
         * @param _domain
         * @param _entries (array)
         */
        const checkDomainEntries = (_domain: string, _entries: string | string[]) => {
          const _domainFilter = validities[_domain];
          
          // Will be true if invalid entry found (negative match) and no valid entry found
          let validEntries = true;
          
          for (let i = 0; i < _entries.length; i++) {
            const entry = _entries[i];
            let negativeValidityHit = false;

            for (let i = 0; i < _domainFilter.length; i++) {
              const validity = _domainFilter[i];
              const validityId = validity.replace("!", "");

              // Positive filter matched
              if (positive && entry === validityId) {
                return true;
              } else if (!positive && entry === validityId) {
                // Negative validity matched (e.g. !V60)
                negativeValidityHit = true;
                validEntries = false;
                break;
              }
            }
            
            if (!positive && !negativeValidityHit) {
              // Entry found that was not in negative validity list
              return true;
            }
          }
          
          if (positive) {
            // If this is true, no positive match was found
            return false;
          }
          
          // We have negative validity (e.g. !V60)
          // and no entry match any of the negative validity 
          return validEntries; 
        };

        if (domainEntries.length) {
          matched[domain] = checkDomainEntries(domain, domainEntries);
        } 
        else if (filterDomain) {
          // Check on main domain
          matched[domain] = checkFilter(domain, filterDomain.entry as string);
        } 
        else {
          matched[domain] = true;
        }
      } else {
        //
        // Domain filter not set => default to true

        //
        matched[domain] = true;
      }
    }

    // If any match groups were negative => return false
    for (const domain in matched) {
      if (matched[domain] === false) {
        return false;
      }
    }

    return true;
  }
}
